import React, { useState, useEffect } from 'react';
import { AlertCircle, Calendar, ChevronRight, ChevronDown } from 'lucide-react';
import { useApi } from '../hooks/useApi';

const MARKETS = ['fix_i', 'fix_ii', 'ida1', 'sk_d1', 'ida2', 'ida3', 'sk_d', 'bm', 'sk_actual', 'bm_cost'];
const MARKET_LABELS = {
  fix_i: 'Fix I',
  fix_ii: 'Fix II',
  ida1: 'IDA1',
  sk_d1: 'SK-1',
  ida2: 'IDA2',
  ida3: 'IDA3',
  sk_d: 'SK-0',
  bm: 'BM-0',
  sk_actual: 'SK',
  bm_cost: 'BM',
};
const SK_MARKETS = new Set(['sk_d1', 'sk_d', 'sk_actual']);

// Column background colors — each price column a distinct hue, SK all same amber
const COL_BG = {
  base:      'rgba(204,251,241,0.45)', // teal-100
  fix_i:     'rgba(219,234,254,0.45)', // blue-100
  fix_ii:    'rgba(199,210,254,0.40)', // indigo-200
  ida1:      'rgba(196,181,253,0.30)', // violet-300
  sk_d1:     'rgba(253,230,138,0.30)', // amber-200
  ida2:      'rgba(249,168,212,0.25)', // pink-300
  ida3:      'rgba(252,165,165,0.25)', // red-300
  sk_d:      'rgba(253,230,138,0.30)', // amber-200
  bm:        'rgba(253,186,116,0.30)', // orange-300
  sk_actual: 'rgba(253,230,138,0.30)', // amber-200
  bm_cost:   'rgba(253,186,116,0.30)', // orange-300 (same as bm)
};
const COL_HEADER_BG = {
  base:      'rgba(153,246,228,0.60)', // teal-200
  fix_i:     'rgba(191,219,254,0.60)', // blue-200
  fix_ii:    'rgba(165,180,252,0.55)', // indigo-300
  ida1:      'rgba(167,139,250,0.40)', // violet-400
  sk_d1:     'rgba(253,224,71,0.40)',  // amber-300
  ida2:      'rgba(244,114,182,0.35)', // pink-400
  ida3:      'rgba(248,113,113,0.35)', // red-400
  sk_d:      'rgba(253,224,71,0.40)',  // amber-300
  bm:        'rgba(251,146,60,0.40)',  // orange-400
  sk_actual: 'rgba(253,224,71,0.40)',  // amber-300
  bm_cost:   'rgba(251,146,60,0.40)',  // orange-400 (same as bm)
};

// Lighter column colors for quarter-hour rows (same hue, lower opacity)
const COL_BG_QH = {
  base:      'rgba(204,251,241,0.20)',
  fix_i:     'rgba(219,234,254,0.20)',
  fix_ii:    'rgba(199,210,254,0.18)',
  ida1:      'rgba(196,181,253,0.13)',
  sk_d1:     'rgba(253,230,138,0.13)',
  ida2:      'rgba(249,168,212,0.11)',
  ida3:      'rgba(252,165,165,0.11)',
  sk_d:      'rgba(253,230,138,0.13)',
  bm:        'rgba(253,186,116,0.13)',
  sk_actual: 'rgba(253,230,138,0.13)',
  bm_cost:   'rgba(253,186,116,0.13)',
};

const QH_LABELS = ['00', '15', '30', '45'];

function Prices() {
  const { axiosPost } = useApi();
  const darkMode = typeof document !== 'undefined' && document.body.classList.contains('global-dark-mode');

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split('T')[0];
  const daysAgo14 = new Date(today);
  daysAgo14.setDate(daysAgo14.getDate() - 14);
  const daysAgo14Str = daysAgo14.toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(daysAgo14Str);
  const [endDate, setEndDate] = useState(tomorrowStr);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const [expandedDates, setExpandedDates] = useState(new Set());
  const [expandedHours, setExpandedHours] = useState(new Set());

  const handleFetch = async () => {
    setLoading(true);
    setError('');
    setData(null);

    try {
      const response = await axiosPost('/prices', {
        start_date: startDate,
        end_date: endDate,
      });

      if (response.data.status === 'success') {
        setData(response.data.dates);
        setExpandedDates(new Set());
        setExpandedHours(new Set());
      } else {
        setError(response.data.message || 'Failed to fetch prices');
      }
    } catch (err) {
      console.error('Prices error:', err);
      setError(err.message || 'Failed to fetch prices');
    } finally {
      setLoading(false);
    }
  };

  // Silent refresh — updates data only, preserves expanded state and scroll
  const silentRefresh = async () => {
    try {
      const response = await axiosPost('/prices', {
        start_date: startDate,
        end_date: endDate,
      });
      if (response.data.status === 'success') {
        setData(response.data.dates);
      }
    } catch (err) {
      // Silent — don't update error state on background refresh
    }
  };

  useEffect(() => {
    handleFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    const interval = setInterval(silentRefresh, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const toggleDate = (dateStr) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(dateStr)) {
        next.delete(dateStr);
        setExpandedHours((prevH) => {
          const nextH = new Set(prevH);
          for (const key of prevH) {
            if (key.startsWith(dateStr + '/')) nextH.delete(key);
          }
          return nextH;
        });
      } else {
        next.add(dateStr);
      }
      return next;
    });
  };

  const toggleHour = (dateStr, hour) => {
    const key = `${dateStr}/${hour}`;
    setExpandedHours((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // SK values: multiply by 4, negate, round to integer
  const skTransform = (val) => {
    if (val === null || val === undefined) return null;
    return Math.ceil(-val * 4);
  };

  const displayVal = (val, market) => {
    if (SK_MARKETS.has(market)) return skTransform(val);
    return val;
  };

  const formatPrice = (val, market) => {
    const v = displayVal(val, market);
    if (v === null || v === undefined) return '-';
    if (SK_MARKETS.has(market)) return v.toString();
    return val.toFixed(2);
  };

  // BASE is a daily product: VWAP and traded volume, e.g. "550.00 PLN / 20 MW"
  const formatBase = (base) => {
    if (!base || base.price === null || base.price === undefined) return '-';
    const price = `${base.price.toFixed(2)} PLN`;
    if (base.volume === null || base.volume === undefined) return price;
    const volume = Number.isInteger(base.volume) ? base.volume : base.volume.toFixed(1);
    return `${price} / ${volume} MW`;
  };

  const valColor = (val, market) => {
    const v = displayVal(val, market);
    if (v === null || v === undefined) return 'text-gray-400';
    if (SK_MARKETS.has(market)) {
      if (v > 0) return 'text-red-600';
      if (v < 0) return 'text-green-600';
      return darkMode ? 'text-slate-300' : 'text-gray-700';
    }
    if (v < 0) return 'text-red-600';
    return darkMode ? 'text-slate-100' : 'text-gray-800';
  };

  const getHeaderBg = (market) => darkMode
    ? COL_HEADER_BG[market].replace(/0\.\d+\)/, '0.22)')
    : COL_HEADER_BG[market];

  const getColumnBg = (market, quarter = false) => {
    const source = quarter ? COL_BG_QH[market] : COL_BG[market];
    if (!darkMode) return source;
    return source.replace(/0\.\d+\)/, quarter ? '0.12)' : '0.18)');
  };

  return (
    <div className={`min-h-screen p-8 ${darkMode ? 'bg-transparent' : 'bg-gray-50'}`}>
      <div className="max-w-[1600px] mx-auto space-y-6">
        <div>
          <h1 className={`text-4xl font-bold mb-2 ${darkMode ? 'text-slate-100' : 'text-gray-800'}`}>Market Prices</h1>
          <p className={darkMode ? 'text-slate-400' : 'text-gray-600'}>View prices across all auctions: BASE, Fix I, Fix II, IDA1, IDA2, IDA3, Balancing Market</p>
        </div>

        <div className={`${darkMode ? 'bg-slate-900 border border-slate-800 shadow-xl shadow-slate-950/20' : 'bg-white shadow-sm'} rounded-xl p-6`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
            <div>
              <label className={`flex items-center space-x-2 text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                <Calendar size={16} />
                <span>Start Date</span>
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'border-slate-700 bg-slate-950 text-slate-100' : 'border-gray-300'}`}
              />
            </div>
            <div>
              <label className={`flex items-center space-x-2 text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                <Calendar size={16} />
                <span>End Date</span>
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'border-slate-700 bg-slate-950 text-slate-100' : 'border-gray-300'}`}
              />
            </div>
            <button
              onClick={handleFetch}
              disabled={loading}
              className="w-1/2 px-8 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                  <span>Loading...</span>
                </>
              ) : (
                <span>Show Prices</span>
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {data && data.length > 0 && (
          <div className={`${darkMode ? 'bg-slate-900 border border-slate-800 shadow-xl shadow-slate-950/20' : 'bg-white shadow-sm'} rounded-xl overflow-x-auto`}>
            <table className="w-full text-sm" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr>
                  <th className={`text-left py-3 px-4 font-semibold min-w-[180px] sticky top-0 z-10 ${darkMode ? 'text-slate-200 bg-slate-950 border-b-2 border-r border-slate-700' : 'text-gray-700 bg-gray-100 border-b-2 border-r border-gray-300'}`}>
                    Date / Hour
                  </th>
                  <th
                    className={`text-center py-3 px-4 font-semibold min-w-[80px] sticky top-0 z-10 ${darkMode ? 'text-slate-200 border-b-2 border-l border-slate-700' : 'text-gray-700 border-b-2 border-l border-gray-300'}`}
                    style={{ backgroundColor: getHeaderBg('base') }}
                  >
                    BASE
                  </th>
                  {MARKETS.map((m) => (
                    <th
                      key={m}
                      className={`text-center py-3 px-4 font-semibold min-w-[80px] sticky top-0 z-10 ${darkMode ? 'text-slate-200 border-b-2 border-l border-slate-700' : 'text-gray-700 border-b-2 border-l border-gray-300'}`}
                      style={{ backgroundColor: getHeaderBg(m) }}
                    >
                      {MARKET_LABELS[m]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((dateEntry) => {
                  const dateStr = dateEntry.date;
                  const isDateExpanded = expandedDates.has(dateStr);
                  const dayOfWeek = new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' });

                  return (
                    <React.Fragment key={dateStr}>
                      {/* Date row */}
                      <tr
                        className="cursor-pointer hover:brightness-95 transition-all"
                        style={{ backgroundColor: darkMode ? '#0f172a' : '#f0f4f8' }}
                        onClick={() => toggleDate(dateStr)}
                      >
                        <td className={`py-3 px-4 font-semibold border-b border-r ${darkMode ? 'text-slate-100 border-slate-700 bg-slate-900/95' : 'text-gray-800 border-gray-300 bg-gray-100/80'}`}>
                          <div className="flex items-center">
                            {isDateExpanded
                              ? <ChevronDown className={`w-4 h-4 mr-2 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`} />
                              : <ChevronRight className={`w-4 h-4 mr-2 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`} />
                            }
                            {dateStr}
                            <span className={`ml-2 text-xs font-normal ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>{dayOfWeek}</span>
                          </div>
                        </td>
                        <td
                          className={`py-3 px-4 text-center font-semibold text-xs whitespace-nowrap border-b border-l ${darkMode ? 'border-slate-700' : 'border-gray-300'} ${valColor(dateEntry.base?.price, 'base')}`}
                          style={{ backgroundColor: getColumnBg('base') }}
                        >
                          {formatBase(dateEntry.base)}
                        </td>
                        {MARKETS.map((m) => (
                          <td
                            key={m}
                            className={`py-3 px-4 text-center font-semibold text-xs border-b border-l ${darkMode ? 'border-slate-700' : 'border-gray-300'} ${valColor(dateEntry.daily_avg[m], m)}`}
                            style={{ backgroundColor: getColumnBg(m) }}
                          >
                            {formatPrice(dateEntry.daily_avg[m], m)}
                          </td>
                        ))}
                      </tr>

                      {/* Hourly rows */}
                      {isDateExpanded && dateEntry.hours.map((hourEntry) => {
                        const hour = hourEntry.hour;
                        const hourKey = `${dateStr}/${hour}`;
                        const isHourExpanded = expandedHours.has(hourKey);
                        const displayHour = `${String(hour).padStart(2, '0')}:00 - ${String(hour + 1 === 24 ? 0 : hour + 1).padStart(2, '0')}:00`;
                        const hasAnyPrice = MARKETS.some((m) => hourEntry.hourly[m] !== null);

                        if (!hasAnyPrice) return null;

                        return (
                          <React.Fragment key={hourKey}>
                            <tr
                              className="cursor-pointer hover:brightness-95 transition-all"
                              onClick={() => toggleHour(dateStr, hour)}
                            >
                              <td
                                className={`py-2 px-4 pl-8 border-b border-r ${darkMode ? 'text-slate-300 border-slate-800' : 'text-gray-600 border-gray-200'}`}
                                style={{ backgroundColor: darkMode ? (hour % 2 === 0 ? '#111827' : '#0f172a') : (hour % 2 === 0 ? '#f9fafb' : '#fff') }}
                              >
                                <div className="flex items-center">
                                  {isHourExpanded
                                    ? <ChevronDown className={`w-3 h-3 mr-1.5 ${darkMode ? 'text-slate-500' : 'text-gray-400'}`} />
                                    : <ChevronRight className={`w-3 h-3 mr-1.5 ${darkMode ? 'text-slate-500' : 'text-gray-400'}`} />
                                  }
                                  <span className="font-mono text-xs">{displayHour}</span>
                                </div>
                              </td>
                              <td
                                className={`border-b border-l ${darkMode ? 'border-slate-800' : 'border-gray-200'}`}
                                style={{ backgroundColor: getColumnBg('base') }}
                              />
                              {MARKETS.map((m) => (
                                <td
                                  key={m}
                                  className={`py-2 px-4 text-center font-mono text-xs border-b border-l ${darkMode ? 'border-slate-800' : 'border-gray-200'} ${valColor(hourEntry.hourly[m], m)}`}
                                  style={{ backgroundColor: getColumnBg(m) }}
                                >
                                  {formatPrice(hourEntry.hourly[m], m)}
                                </td>
                              ))}
                            </tr>

                            {/* Quarter rows */}
                            {isHourExpanded && hourEntry.quarters.map((qEntry) => {
                              const qh = qEntry.qh;
                              const minuteStart = QH_LABELS[qh];
                              const minuteEnd = qh < 3 ? QH_LABELS[qh + 1] : '00';
                              const nextHour = qh < 3 ? hour : (hour + 1 === 24 ? 0 : hour + 1);
                              const label = `${String(hour).padStart(2, '0')}:${minuteStart} - ${String(nextHour).padStart(2, '0')}:${minuteEnd}`;
                              const hasAnyQPrice = MARKETS.some((m) => qEntry[m] !== null);

                              if (!hasAnyQPrice) return null;

                              return (
                                <tr key={`${hourKey}/q${qh}`}>
                                  <td className={`py-1.5 px-4 pl-14 border-b border-r ${darkMode ? 'text-slate-500 border-slate-800 bg-slate-950/70' : 'text-gray-400 border-gray-200'}`}>
                                    <span className="font-mono text-xs">{label}</span>
                                  </td>
                                  <td
                                    className={`border-b border-l ${darkMode ? 'border-slate-800' : 'border-gray-200'}`}
                                    style={{ backgroundColor: getColumnBg('base', true) }}
                                  />
                                  {MARKETS.map((m) => (
                                    <td
                                      key={m}
                                      className={`py-1.5 px-4 text-center font-mono text-xs border-b border-l ${darkMode ? 'border-slate-800' : 'border-gray-200'} ${valColor(qEntry[m], m)}`}
                                      style={{ backgroundColor: getColumnBg(m, true) }}
                                    >
                                      {formatPrice(qEntry[m], m)}
                                    </td>
                                  ))}
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {data && data.length === 0 && (
          <div className={`text-center py-8 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>No price data found for the selected date range.</div>
        )}
      </div>
    </div>
  );
}

export default Prices;
